import type { RGBColor } from "react-color";
import type { StyleLibrary } from "schemas/generated/project";

import React, { useState } from "react";

import { Field, FieldError } from "@replo/design-system/components/field/Field";
import { Input } from "@replo/design-system/components/input/Input";
import {
  PopoverContent,
  PopoverRoot,
  PopoverTrigger,
} from "@replo/design-system/components/popover/Popover";
import { Toggle } from "@replo/design-system/components/toggle/Toggle";
import { ToggleGroup } from "@replo/design-system/components/toggle/ToggleGroup";
import { skipToken } from "@tanstack/react-query";
import { PickerComponent } from "@v2/components/CustomPicker";
import { useDesignLibraryColors } from "@v2/features/design-library/useDesignLibraryColors";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { stylesRouter } from "@v2/trpc/routes/styles";
import {
  getFormattedColor,
  getFormattedColorWithoutOpacity,
  getHex8Color,
  getHexColor,
  getInitialColorValue,
  getRGB,
} from "@v2/utils/colors";
import { X } from "lucide-react";
import useMeasure from "react-use-measure";
import { useOverridableState } from "replo-runtime/shared/hooks/useOverridableState";
import { useDebouncedCallback } from "replo-utils/react/use-debounced-callback";

interface ColorModifierProps {
  value?: string | null;
  onChange: (color: string | null) => void;
}

interface ColorGridProps {
  styleLibrary: StyleLibrary | null;
  onColorSelect: (color: string) => void;
}

const ColorGrid: React.FC<ColorGridProps> = ({
  styleLibrary,
  onColorSelect,
}) => {
  const { allColors } = useDesignLibraryColors(styleLibrary, {
    includeEmpty: false,
  });

  if (allColors.length === 0) {
    return (
      <div className="flex flex-col justify-center items-center text-center text-muted text-xs w-full">
        <div className="mb-1">No design library colors available</div>
        <div className="text-2xs">
          Set up colors in your design library to see them here
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-3 gap-2 p-1 w-full overflow-y-auto">
      {allColors.map((color) => (
        <button
          key={color.key}
          className="flex flex-col items-center p-1.5 rounded-lg border-[0.5px] border-black/10 hover:bg-surface transition-colors w-full"
          onClick={() => onColorSelect(color.value)}
          title={`${color.name} - ${getFormattedColorWithoutOpacity(color.value)?.toUpperCase()}`}
        >
          <div
            className="w-full aspect-square rounded-lg border-[0.5px] border-black/10 mb-2 max-w-[50px]"
            style={{ backgroundColor: color.value }}
          />
          <div className="flex flex-col items-center text-2xs">
            <div className="text-muted font-medium mb-1">{color.name}</div>
            <div className="font-mono text-2xs text-muted uppercase">
              {getFormattedColorWithoutOpacity(color.value)?.toUpperCase()}
            </div>
          </div>
        </button>
      ))}
    </div>
  );
};

interface ColorTriggerButtonProps {
  currentColor: string | null;
  disabled?: boolean;
  size?: "sm" | "md";
  isOpen?: boolean;
}

const ColorTriggerButton: React.FC<
  ColorTriggerButtonProps & {
    onClick: () => void;
    onColorChange: (color: string | null) => void;
  }
> = React.memo(
  ({
    currentColor,
    disabled = false,
    onClick,
    onColorChange,
    isOpen = false,
  }) => {
    const colorSwatch = (
      <div
        className="w-4 h-4 rounded border border-border flex-shrink-0"
        style={{ backgroundColor: currentColor ?? undefined }}
      />
    );

    // Show 6-digit hex in input, but maintain 8-digit hex for internal use
    const externalDisplayValue = currentColor
      ? getHexColor(currentColor).toUpperCase()
      : "";

    /*
     * NOTE (Kevin, 2025-12-17): Use local draft state that only commits on blur/Enter,
     * not on every keystroke. This prevents intermediate valid hex lengths (3, 4 chars)
     * from being committed while the user is typing a longer hex code (6, 8 chars).
     * See REPL-21305.
     *
     * We track a separate "editing draft" only while focused. When not focused,
     * the displayed value is derived directly from externalDisplayValue (no effect needed).
     */
    const [editingDraft, setEditingDraft] = React.useState<string | null>(null);
    const [error, setError] = React.useState<string | null>(null);
    const previousColorRef = React.useRef(currentColor);

    // NOTE (Derek, 2026-01-28, REPL-22886): Clear error when color changes externally
    // (e.g. selecting from the style grid popup)
    if (previousColorRef.current !== currentColor) {
      previousColorRef.current = currentColor;
      if (error != null) {
        setError(null);
        setEditingDraft(null);
      }
    }

    // NOTE (Kevin, 2025-12-17): Derive displayed value - use editing draft while focused, otherwise external value
    const displayedValue = editingDraft ?? externalDisplayValue;

    const handleChange = React.useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        // NOTE (Kevin, 2025-12-17): Only update local draft on change, don't commit to parent
        setEditingDraft(event.target.value);
        setError(null);
      },
      [],
    );

    // NOTE (Kevin, 2025-12-17): Validate and commit the color (called on blur and Enter)
    const commitColor = React.useCallback(() => {
      if (editingDraft === null) {
        return;
      }

      const trimmedValue = editingDraft.replace(/^#/, "").trim();

      if (trimmedValue.length === 0) {
        setEditingDraft(null);
        return;
      }
      /*
       * NOTE (Kevin, 2025-12-17): Only accept 6 or 8 character hex codes.
       * We don't accept 3-char (#RGB) or 4-char (#RGBA) shorthand because they cause
       * confusing behavior when editing - e.g., deleting from #FF5C1C to #FF5C would
       * be interpreted as #FFFF55CC (each char doubled) instead of incomplete input.
       */
      const isValidHexLength =
        trimmedValue.length === 6 || trimmedValue.length === 8;
      const isValidHexChars = /^[\dA-Fa-f]*$/.test(trimmedValue);

      if (isValidHexLength && isValidHexChars) {
        /*
         * NOTE (Kurt, 2025-08-26): Preserve the current alpha value when changing colors
         * Extract alpha from current color and apply it to the new color value
         * This ensures alpha is maintained when switching between colors
         */
        const currentRgbColor = getRGB(currentColor);
        const newRgbColor = getRGB(editingDraft);
        newRgbColor.a = currentRgbColor.a ?? 1;
        const hex8Value = getHex8Color(newRgbColor);
        onColorChange(hex8Value);
        setError(null);
        setEditingDraft(null);
      } else {
        setError("Enter a valid hex code (e.g. #FF5C1C)");
      }
    }, [editingDraft, currentColor, onColorChange]);

    const handleFocus = React.useCallback(() => {
      // NOTE (Kevin, 2025-12-17): Start editing with current external value
      setEditingDraft(externalDisplayValue);
    }, [externalDisplayValue]);

    const handleBlur = React.useCallback(() => {
      commitColor();
    }, [commitColor]);

    const inputRef = React.useRef<HTMLInputElement>(null);

    const handleClick = () => {
      onClick();
      setTimeout(() => {
        inputRef.current?.focus();
        if (!isOpen) {
          inputRef.current?.select();
        }
      }, 0);
    };

    return (
      <Field size="xs">
        <div onClick={handleClick}>
          <Input
            ref={inputRef}
            value={displayedValue}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                commitColor();
              }
            }}
            size="sm"
            disabled={disabled}
            invalid={error != null}
            startEnhancer={colorSwatch}
            placeholder="#000000"
            endEnhancer={
              <X
                className="text-muted hover:text-default transition-colors cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  setError(null);
                  setEditingDraft(null);
                  onColorChange(null);
                }}
              />
            }
            className="w-full"
          />
        </div>
        {/* NOTE (Derek, 2026-01-28, REPL-22886): Stop pointer/click events from
            bubbling to the Popover trigger, which would otherwise focus the input */}
        <div
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <FieldError size="xs">{error}</FieldError>
        </div>
      </Field>
    );
  },
);

ColorTriggerButton.displayName = "ColorTriggerButton";

const ColorSelector: React.FC<ColorModifierProps> = ({ value, onChange }) => {
  const initialColor = value;
  const projectId = useCurrentProjectId();
  const { data } = stylesRouter.getStyleLibrary.useQuery(
    projectId ? { projectId } : skipToken,
  );
  const styleLibrary = data?.styleLibrary ?? null;

  const [currentValue, setCurrentValue] = useOverridableState(
    initialColor ?? null,
    onChange,
  );

  const debouncedSetCurrentValue = useDebouncedCallback(
    (color: string | null) => {
      setCurrentValue(color);
    },
    500,
  );

  const [isOpen, setIsOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState<string>("styles");
  // NOTE (Kurt, 2025-01-27): Only sync external changes when the popover is closed
  // to prevent feedback loops that would cause the picker to "jump" during user interaction.
  // When the user drags the picker, it updates the selected element, which would normally
  // trigger this effect and reset the picker position mid-drag without this guard.
  // Only sync external changes when the popover is closed
  const [triggerRef, { width: triggerWidth }] = useMeasure();
  const triggerElementRef = React.useRef<HTMLDivElement | null>(null);

  // NOTE (Kurt, 2025-08-24): We want the popover to be at least 250px wide to avoid
  // the popover from being too narrow when the user is selecting a color from the styles tab.
  const effectiveTriggerWidth = Math.max(triggerWidth, 250);

  const [pickerColor, setPickerColor] = useState<RGBColor>(() => {
    return getInitialColorValue(currentValue);
  });

  const handleColorPickerChange = React.useCallback(
    (rgb: RGBColor) => {
      setPickerColor(rgb);
      const hexColor = getFormattedColor(getHexColor(rgb));
      // Note (Noah, 2025-09-11): Debounce this callback so that we don't get
      // a new color on every drag event from the picker, just once every 500ms
      if (hexColor) {
        debouncedSetCurrentValue(hexColor);
      }
    },
    [debouncedSetCurrentValue],
  );

  const handleDesignSystemStyleColorSelect = React.useCallback(
    (color: string) => {
      // Note (Noah, 2025-09-11): Do not debounce here, we want to save the color immediately
      setCurrentValue(color);
      setIsOpen(false);

      const newPickerColor = getRGB(color);
      if (newPickerColor) {
        setPickerColor(newPickerColor);
      }
    },
    [setCurrentValue],
  );

  const shouldAllowClose = React.useRef(true);

  /**
   * Intercepts popover close attempts to prevent closes when clicking trigger.
   *
   * When the trigger area receives a pointerdown, we set shouldAllowClose to false.
   * Base UI's onOpenChange fires after pointerdown, so this handler checks the flag
   * and blocks the close if it was triggered by a trigger click, allowing the popover
   * to stay open when user clicks on the input or color swatch.
   */
  const handleOpenChange = React.useCallback((newIsOpen: boolean) => {
    if (!newIsOpen && !shouldAllowClose.current) {
      shouldAllowClose.current = true;
      return;
    }
    shouldAllowClose.current = true;
    setIsOpen(newIsOpen);
  }, []);

  return (
    <div
      ref={(element) => {
        triggerRef(element);
        // Cast to mutable ref to allow assignment to current property, this is because
        // useMeasure returns a readonly ref
        (
          triggerElementRef as React.MutableRefObject<HTMLDivElement | null>
        ).current = element;
      }}
      className="w-full"
    >
      <PopoverRoot open={isOpen} onOpenChange={handleOpenChange}>
        <PopoverTrigger
          render={<div className="w-full" />}
          onPointerDownCapture={() => {
            shouldAllowClose.current = false;
          }}
        >
          <ColorTriggerButton
            currentColor={currentValue}
            onClick={() => setIsOpen(true)}
            onColorChange={(newColor) => setCurrentValue(newColor)}
            isOpen={isOpen}
          />
        </PopoverTrigger>
        <PopoverContent
          side="top"
          sideOffset={8}
          showCloseButton={false}
          UNSAFE_className="bg-transparent border-0 p-0 shadow-none w-auto rounded-none gap-0"
        >
          <div
            className="flex flex-col items-start gap-3 bg-white border-[0.5px] border-[#CBD5E1] rounded-lg shadow-[4px_4px_16px_rgba(0,0,0,0.06)]"
            style={{ width: effectiveTriggerWidth }}
          >
            <div className="w-full p-3 pb-0">
              <ToggleGroup
                value={[selectedTab]}
                onValueChange={(newValue) => {
                  if (newValue.length > 0) {
                    setSelectedTab(newValue[0]!);
                  }
                }}
                className="w-full"
              >
                <Toggle value="styles" size="sm" className="flex-1">
                  Styles
                </Toggle>
                <Toggle value="custom" size="sm" className="flex-1">
                  Custom
                </Toggle>
              </ToggleGroup>
            </div>

            {selectedTab === "styles" ? (
              <div className="px-2 pb-2 w-full">
                <ColorGrid
                  styleLibrary={styleLibrary}
                  onColorSelect={handleDesignSystemStyleColorSelect}
                />
              </div>
            ) : (
              <div className="w-full px-2 pb-2">
                <PickerComponent
                  color={pickerColor}
                  onChange={({ rgb }) => handleColorPickerChange(rgb)}
                />
              </div>
            )}
          </div>
        </PopoverContent>
      </PopoverRoot>
    </div>
  );
};

export default ColorSelector;
